'use client'

import React, { useEffect, useState, useCallback, useRef } from 'react'
import { useParams, useRouter } from 'next/navigation'
import { useForm, FormProvider, Controller } from 'react-hook-form'
import { zodResolver } from '@/app/lib/zodResolver'
import { z } from 'zod'
import { isValidPhoneNumber, parsePhoneNumber } from 'libphonenumber-js'
import { useLoanComparatorStore, useServerDataLoaded } from '@/app/stores/loan/loan-comparator.store'
import {
    updateBorrowerIdentity,
    validatePhone,
    validateEmail,
    updateQuoteStatus,
    createDelayedCallTask,
} from '@/app/endpoints/loanComparatorEndpoints'
import { useTracking } from '@/app/contexts/tracking-context'
import { useLeadConversionTracking } from '@/app/hooks/useLeadConversionTracking'
import { conversionValueEnabled } from '@/app/utils/health-tracking'
import { LoanTrackingEvents } from '@/app/constants/loanTracking'
import { AeTrackingEvents } from '@/app/constants/loanAeTracking'
import { PhoneValidationTrackingEvents } from '@/app/constants/funnelTracking'
import { buildAeParams } from '@/app/utils/loan/loan-ae-tracking'
import { LOAN_STATUS } from '@/app/constants/loanTypes'
import { mapBorrowerForSubmission } from '@/app/utils/loan/borrower-mapper'
import { getCookie } from '@/app/utils/cookie-utils'
import { normalizePhone } from '@/app/utils/security-utils'
import { withRequiredAsterisk } from '@/app/utils/required-label'
import { CookiesEnum } from '@/app/enums/Cookies'
import AppFormInput from '@/app/components/atomic/AppFormInput'
import AppCheckbox from '@/app/components/atomic/Input/AppCheckbox'
import ContactConsentCheckboxes from '@/app/components/atomic/molecules/ContactConsentCheckboxes'
import FormSectionHeading from '@/app/components/atomic/molecules/FormSectionHeading'
import { usePhoneConsentMode } from '@/app/hooks/usePhoneConsentMode'
import CardSelect, { type CardSelectOption } from '@/app/components/atomic/CardSelect'
import AppButtonNew from '@/app/components/atomic/molecules/AppButton'
import AppIcon from '@/app/components/atomic/AppIcon'

// Validation schema aligned with Symfony
const identityFormSchema = z
    .object({
        lastname: z
            .string()
            .min(1, 'Le nom est requis')
            .min(2, 'Le nom doit contenir au moins 2 caractères')
            .max(30, 'Le nom ne doit pas dépasser 30 caractères')
            .regex(/^[a-zA-ZÀ-ÿ\s'-]+$/, 'Votre nom semble invalide. Veuillez vérifier votre saisie.'),
        firstname: z
            .string()
            .min(1, 'Le prénom est requis')
            .min(2, 'Le prénom doit contenir au moins 2 caractères')
            .max(30, 'Le prénom ne doit pas dépasser 30 caractères')
            .regex(/^[a-zA-ZÀ-ÿ\s'-]+$/, 'Votre prénom semble invalide. Veuillez vérifier votre saisie.'),
        email: z.string().min(1, "L'email est requis").email('Veuillez entrer un email valide'),
        phone: z
            .string()
            .min(1, 'Le téléphone est requis')
            .refine(
                (val) => {
                    const clean = val.replace(/\s/g, '')
                    if (!clean || /^0[0-9]0{8}$/.test(clean)) return false
                    return isValidPhoneNumber(clean, 'FR')
                },
                { message: 'Veuillez entrer un numéro de téléphone valide' }
            ),
        gender: z.number({ error: 'Veuillez indiquer votre civilité' }),
    })
    .refine((data) => data.lastname.toLowerCase() !== data.firstname.toLowerCase(), {
        message: 'Les noms et prénoms sont identiques.',
        path: ['firstname'],
    })

type IdentityFormData = z.infer<typeof identityFormSchema>

// Civilité — envoyée au backend (gender) avec le payload d'identité
const GENDER_FEMALE = 1
const GENDER_MALE = 0
const civilityOptions: CardSelectOption<number>[] = [
    { label: 'Madame', value: GENDER_FEMALE },
    { label: 'Monsieur', value: GENDER_MALE },
]

const scrollToFirstError = () => {
    setTimeout(() => {
        const errorMessage = document.querySelector('#identity-form .text-red-600')
        if (errorMessage) {
            const group = errorMessage.closest('.form-group') || errorMessage.parentElement
            group?.scrollIntoView({ behavior: 'smooth', block: 'center' })
            ;(group?.querySelector('input') as HTMLElement | null)?.focus({ preventScroll: true })
        }
    }, 100)
}

interface LoanComparatorIdentityFormProps {
    onBack: () => void
    headingTag?: 'h1' | 'h2'
}

export default function LoanComparatorIdentityForm({ onBack, headingTag: HeadingTag = 'h1' }: LoanComparatorIdentityFormProps) {
    const params = useParams()
    const router = useRouter()
    const token = params.token as string

    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [isPhoneValidating, setIsPhoneValidating] = useState(false)
    const [isEmailValidating, setIsEmailValidating] = useState(false)
    const [emailValid, setEmailValid] = useState(false)
    const [phoneValid, setPhoneValid] = useState(false)
    const [crossFieldErrorMessage, setCrossFieldErrorMessage] = useState<string | null>(null)
    const [acceptPhoneContact, setAcceptPhoneContact] = useState(false)
    const [acceptEmailContact, setAcceptEmailContact] = useState(false)
    const phoneConsentMode = usePhoneConsentMode()
    const [dataSharingOptin, setDataSharingOptin] = useState(false)
    const [whatsappOptin, setWhatsappOptin] = useState(false)
    const [rgpdExpanded, setRgpdExpanded] = useState(false)
    const phoneVerdictRef = useRef<{ value: string; valid: boolean } | null>(null)
    const emailVerdictRef = useRef<{ value: string; valid: boolean } | null>(null)
    const phoneDebounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
    const emailDebounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
    const hasPrefilledRef = useRef(false)

    const { track, trackLead } = useTracking()
    const trackLeadConversion = useLeadConversionTracking()

    const quote = useLoanComparatorStore((state) => state.quote)
    const setWhatsappOptinFromIdentity = useLoanComparatorStore((state) => state.subscriptionPrefsActions.setWhatsappOptinFromIdentity)
    const setGenderFromIdentity = useLoanComparatorStore((state) => state.subscriptionPrefsActions.setGenderFromIdentity)
    const serverDataLoaded = useServerDataLoaded()

    const formMethods = useForm<IdentityFormData>({
        resolver: zodResolver(identityFormSchema) as any,
        mode: 'onBlur',
        defaultValues: {
            lastname: '',
            firstname: '',
            email: '',
            phone: '',
        },
    })

    const {
        register,
        control,
        formState: { errors },
        setValue,
        setError: setFormError,
        watch,
        trigger,
        getValues,
    } = formMethods


    const cleanPhone = useCallback(() => getValues('phone')?.replace(/\s/g, '') || '', [getValues])
    const cleanEmail = useCallback(() => getValues('email')?.trim().toLowerCase() || '', [getValues])

    // Le blur reformate le champ en international : la clé du verdict API doit survivre à ce changement de forme
    const phoneKey = useCallback(() => {
        const clean = cleanPhone()
        try {
            return normalizePhone(clean)
        } catch {
            return clean
        }
    }, [cleanPhone])

    // Zod validation on each keystroke + debounced API check (300ms) once a valid format is entered (health parity)
    const handlePhoneChange = useCallback(async () => {
        setPhoneValid(false)
        clearTimeout(phoneDebounceRef.current)

        const clean = cleanPhone()
        const key = phoneKey()
        const formatValid = await trigger('phone')
        if (!formatValid || key !== phoneKey()) {
            setIsPhoneValidating(false)
            return
        }

        const verdict = phoneVerdictRef.current
        if (verdict?.value === key) {
            if (verdict.valid) setPhoneValid(true)
            else setFormError('phone', { message: 'Oups ce numéro ne semble pas valide.' })
            return
        }

        phoneDebounceRef.current = setTimeout(async () => {
            setIsPhoneValidating(true)
            try {
                const valid = await validatePhone(clean)
                phoneVerdictRef.current = { value: key, valid }
                track({
                    event: valid
                        ? PhoneValidationTrackingEvents.valid
                        : PhoneValidationTrackingEvents.invalid,
                })
                if (key !== phoneKey()) return
                if (valid) setPhoneValid(true)
                else setFormError('phone', { message: 'Oups ce numéro ne semble pas valide.' })
            } finally {
                setIsPhoneValidating(false)
            }
        }, 300)
    }, [cleanPhone, phoneKey, setFormError, trigger, track])

    // International formatting applied on blur only to avoid moving the caret while typing
    const handlePhoneBlur = useCallback(() => {
        const clean = cleanPhone()
        if (!isValidPhoneNumber(clean, 'FR')) return
        try {
            const parsed = parsePhoneNumber(clean, 'FR')
            if (parsed) {
                setValue('phone', parsed.formatInternational(), { shouldValidate: false })
            }
        } catch {
            // Formatting failed, keep original value
        }
    }, [cleanPhone, setValue])

    // Email validation on change: Zod on each keystroke + debounced API check (anti-spam + CaptainVerify in prod)
    const handleEmailChange = useCallback(async () => {
        setEmailValid(false)
        clearTimeout(emailDebounceRef.current)

        const clean = cleanEmail()
        const formatValid = await trigger('email')
        if (!formatValid || clean !== cleanEmail()) {
            setIsEmailValidating(false)
            return
        }

        const verdict = emailVerdictRef.current
        if (verdict?.value === clean) {
            if (verdict.valid) setEmailValid(true)
            else setFormError('email', { message: 'Cette adresse email ne semble pas valide.' })
            return
        }

        emailDebounceRef.current = setTimeout(async () => {
            setIsEmailValidating(true)
            try {
                const valid = await validateEmail(clean)
                emailVerdictRef.current = { value: clean, valid }
                if (clean !== cleanEmail()) return
                if (valid) setEmailValid(true)
                else setFormError('email', { message: 'Cette adresse email ne semble pas valide.' })
            } finally {
                setIsEmailValidating(false)
            }
        }, 300)
    }, [cleanEmail, setFormError, trigger])

    // Pre-fill form when quote is loaded from store (via PageWrapper)
    useEffect(() => {
        if (!serverDataLoaded || !quote || hasPrefilledRef.current) return
        hasPrefilledRef.current = true

        track({ event: AeTrackingEvents.contactDetails, ...buildAeParams(quote) })

        const mainBorrower = quote.main_borrower
        if (mainBorrower) {
            setValue('lastname', mainBorrower.prospect_info?.last_name || '')
            setValue('firstname', mainBorrower.prospect_info?.first_name || '')
            setValue('email', mainBorrower.prospect_info?.prospect?.email || '')
            setValue('phone', mainBorrower.prospect_info?.phone || '')
            const prefilledGender = mainBorrower.prospect_info?.gender
            if (prefilledGender === GENDER_FEMALE || prefilledGender === GENDER_MALE) {
                setValue('gender', prefilledGender)
            }
            setAcceptPhoneContact(mainBorrower.prospect_info?.prospect?.accept_phone_contact_loan ?? false)
            setAcceptEmailContact(mainBorrower.prospect_info?.prospect?.accept_email_contact_loan ?? false)
        }

        void updateQuoteStatus(token, LOAN_STATUS.IDENTITY_STEP_VISITED).catch((error) => {
            console.error('Failed to update quote status (IDENTITY_STEP_VISITED):', error)
        })
    }, [serverDataLoaded, quote, token, setValue, track])

    // Cleanup debounce timers on unmount
    useEffect(() => {
        return () => {
            clearTimeout(phoneDebounceRef.current)
            clearTimeout(emailDebounceRef.current)
        }
    }, [])

    // Submit handler — uses trigger() then reads errors directly
    const onSubmitClick = useCallback(async () => {
        setCrossFieldErrorMessage(null)

        const isValid = await trigger()

        if (!isValid) {
            scrollToFirstError()
            return
        }

        // trigger() re-runs Zod only, which clears manual API errors; re-apply them so a
        // phone/email the debounced API check rejected can't slip through on submit.
        const phoneVerdict = phoneVerdictRef.current
        const emailVerdict = emailVerdictRef.current
        const phoneRejected = !!phoneVerdict && phoneVerdict.value === phoneKey() && !phoneVerdict.valid
        const emailRejected = !!emailVerdict && emailVerdict.value === cleanEmail() && !emailVerdict.valid
        if (phoneRejected) setFormError('phone', { message: 'Oups ce numéro ne semble pas valide.' })
        if (emailRejected) setFormError('email', { message: 'Cette adresse email ne semble pas valide.' })
        if (phoneRejected || emailRejected) {
            scrollToFirstError()
            return
        }

        const data = getValues()

        // Cross-field safety check (Zod refine should catch this, but just in case)
        if (data.lastname.toLowerCase() === data.firstname.toLowerCase()) {
            setCrossFieldErrorMessage('Les noms et prénoms sont identiques.')
            scrollToFirstError()
            return
        }

        if (!quote?.main_borrower?.id) {
            setError("Impossible de trouver l'emprunteur")
            return
        }

        setSubmitting(true)
        setError(null)

        try {
            // Consent mode off: the WhatsApp optin drives the subscription step
            if (!phoneConsentMode && whatsappOptin) {
                setWhatsappOptinFromIdentity(true)
            }

            // Carry civilité to the subscription step so it doesn't have to be re-entered
            setGenderFromIdentity(data.gender)

            // Envoi du borrower complet (parité Vue) pour passer la validation `loanComparator`
            const borrowerPayload = mapBorrowerForSubmission(quote.main_borrower, 'main', undefined, {
                firstname: data.firstname,
                lastname: data.lastname,
                email: data.email,
                phone: data.phone || '',
            })

            const coldLeadEstimate = await updateBorrowerIdentity({
                ...borrowerPayload,
                gender: data.gender,
                trigger_contact_workflow: true,
                token: token,
                whatsapp_optin: phoneConsentMode ? false : whatsappOptin,
                data_sharing_optin: phoneConsentMode ? true : dataSharingOptin,
                // Consent mode off: nothing was collected, so no consent is written
                ...(phoneConsentMode
                    ? { accept_phone_contact: acceptPhoneContact, accept_email_contact: acceptEmailContact }
                    : {}),
                ttclid: getCookie(CookiesEnum.USER_TTCLID) ?? undefined,
            })

            // Après le POST, jamais avant : un échec ne doit pas compter un lead.
            // Awaited: the Ads user-data tag reads email_hash from pushes made before cold_lead_sent.
            await trackLead(
                AeTrackingEvents.leadGenerated,
                { email: data.email, phone: data.phone },
                buildAeParams(quote)
            )

            // Cold lead (Google Ads) + conversion 3 canaux (Google/Meta/TikTok) — mirrors Vue LoanComparatorIdentityGateway.vue
            track({
                event: LoanTrackingEvents.identity.coldLeadSent,
                ...(conversionValueEnabled() && coldLeadEstimate?.estimatedCommission != null
                    ? { value: coldLeadEstimate.estimatedCommission, currency: coldLeadEstimate.currency }
                    : {}),
            })
            trackLeadConversion({
                googleEvent: LoanTrackingEvents.conversion.gtm,
                productType: 'loan',
                fbEventName: 'Identity sent (API)',
                email: data.email,
                phone: data.phone,
                token,
            })

            // consentement téléphonique refusé → pas d'appel commercial +30 min, le back commente le dossier et lance le drip email
            if (!phoneConsentMode || acceptPhoneContact) {
                createDelayedCallTask(token).catch((err) => console.error('Error creating delayed call task:', err))
            }

            setTimeout(() => {
                router.push(`/resultats-assurance-pret-immobilier/${token}`)
            }, 1000)
        } catch (err) {
            console.error('Error updating borrower:', err)
            setError(err instanceof Error ? err.message : "Une erreur est survenue lors de l'envoi des données")
            setSubmitting(false)
        }
    }, [quote, token, router, trigger, getValues, cleanPhone, phoneKey, cleanEmail, setFormError, track, trackLead, trackLeadConversion, acceptPhoneContact, acceptEmailContact, phoneConsentMode, dataSharingOptin, whatsappOptin, setWhatsappOptinFromIdentity, setGenderFromIdentity])

    const emailIcon = isEmailValidating ? (
        <i className="fa-regular fa-spinner fa-spin" />
    ) : emailValid ? (
        <i className="fa-regular fa-check text-green-700" />
    ) : undefined

    const phoneIcon = isPhoneValidating ? (
        <i className="fa-regular fa-spinner fa-spin" />
    ) : phoneValid ? (
        <i className="fa-regular fa-check text-green-700" />
    ) : undefined

    return (
        <FormProvider {...formMethods}>
            <div className="mb-6">
                <HeadingTag
                    style={{ fontFamily: 'var(--font-recoleta)' }}
                    className="text-[26px] leading-[1.2] font-semibold text-pale m-0"
                >
                    Vos informations
                </HeadingTag>
                <p className="mt-2 text-[14px] text-pale/70">
                    {"Dernière étape : ces informations nous permettent de vous transmettre les offres correspondant à votre profil."}
                </p>
            </div>

            <form
                className="flex flex-col gap-5"
                id="identity-form"
                onSubmit={(e) => e.preventDefault()}
            >
                {phoneConsentMode && <FormSectionHeading number={1}>Qui êtes-vous ?</FormSectionHeading>}

                {/* Civilité — obligatoire, envoyée au back (gender) */}
                <div className="form-group">
                    <label className="block text-[14px] font-medium text-pale p-1">
                        {withRequiredAsterisk('Civilité *')}
                    </label>
                    <Controller
                        name="gender"
                        control={control}
                        render={({ field }) => (
                            <CardSelect<number>
                                options={civilityOptions}
                                value={field.value}
                                onChange={(value) => {
                                    field.onChange(value)
                                    // Pas de blur sur des boutons : re-valider ici pour effacer l'erreur « requis »
                                    void trigger('gender')
                                }}
                                containerClassName="grid grid-cols-1 gap-2 md:grid-cols-2"
                                error={errors.gender}
                            />
                        )}
                    />
                </div>

                {/* Nom / Prénom */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <AppFormInput
                        id="lastname"
                        label="Nom *"
                        variant="loan"
                        placeholder="Ex : Dupont"
                        className="placeholder:font-normal min-h-[48px]"
                        error={errors.lastname}
                        {...register('lastname')}
                    />
                    <AppFormInput
                        id="firstname"
                        label="Prénom *"
                        variant="loan"
                        placeholder="Ex : Jean"
                        className="placeholder:font-normal min-h-[48px]"
                        error={errors.firstname || (crossFieldErrorMessage ? { message: crossFieldErrorMessage } : undefined)}
                        {...register('firstname')}
                    />
                </div>

                {phoneConsentMode && <FormSectionHeading number={2}>Où vous joindre ?</FormSectionHeading>}

                {/* Email / Téléphone */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <AppFormInput
                        id="email"
                        label="Email *"
                        type="email"
                        variant="loan"
                        placeholder="Ex : jean.dupont@gmail.com"
                        className="placeholder:font-normal min-h-[48px]"
                        icon={emailIcon}
                        error={errors.email}
                        {...register('email', {
                            onChange: handleEmailChange,
                        })}
                    />
                    <AppFormInput
                        id="phone"
                        label="Téléphone *"
                        type="tel"
                        variant="loan"
                        placeholder="Ex : 06 78 90 12 34"
                        className="placeholder:font-normal min-h-[48px]"
                        icon={phoneIcon}
                        error={errors.phone}
                        {...register('phone', {
                            onChange: handlePhoneChange,
                            onBlur: handlePhoneBlur,
                        })}
                    />
                </div>

                {phoneConsentMode ? (
                    <div className="flex flex-col gap-4">
                        <FormSectionHeading number={3}>Vos accords</FormSectionHeading>
                        <ContactConsentCheckboxes
                            variant="loan"
                            acceptPhoneContact={acceptPhoneContact}
                            acceptEmailContact={acceptEmailContact}
                            onPhoneContactChange={setAcceptPhoneContact}
                            onEmailContactChange={setAcceptEmailContact}
                        />
                    </div>
                ) : (
                    <div className="flex flex-col gap-3">
                        <AppCheckbox
                            fieldName="dataSharingOptin"
                            value={dataSharingOptin}
                            variant="card"
                            labelClassName="font-normal"
                            onChange={(checked) => {
                                setDataSharingOptin(checked as boolean)
                                track({ event: LoanTrackingEvents.identity.legalCheckboxClicked })
                            }}
                        >
                            J&apos;autorise Réassurez-moi.fr à utiliser mes informations pour le traitement de ma demande de devis <span className="text-red-700">*</span>
                        </AppCheckbox>

                        <AppCheckbox
                            fieldName="whatsappOptin"
                            value={whatsappOptin}
                            variant="card"
                            labelClassName="font-normal"
                            description="En cochant cette case vous acceptez de recevoir une réponse directement sur votre compte WhatsApp"
                            onChange={(checked) => {
                                setWhatsappOptin(checked as boolean)
                                track({ event: LoanTrackingEvents.identity.whatsappCheckboxClicked })
                            }}
                        >
                            Je souhaite être recontacté par WhatsApp <i className="fab fa-whatsapp text-[#25D366]" />
                        </AppCheckbox>
                    </div>
                )}

                {/* Précédent + Submit */}
                <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center">
                    <AppButtonNew type="button" theme="outline-rounded" icon="fa-chevron-left" iconType="regular" onClick={onBack}>
                        Précédent
                    </AppButtonNew>
                    <div className="sm:flex-1">
                        <AppButtonNew
                            theme="orange"
                            arrowPosition="before"
                            wFull
                            type="button"
                            icon={submitting ? 'fa-spinner fa-spin' : undefined}
                            onClick={onSubmitClick}
                            disabled={(!phoneConsentMode && !dataSharingOptin) || submitting || isPhoneValidating || isEmailValidating}
                        >
                            {submitting ? 'Envoi en cours...' : 'Accéder à mes résultats'}
                        </AppButtonNew>
                    </div>
                </div>

                {/* Legal small text — below CTA */}
                {phoneConsentMode ? (
                    <div className="text-center" data-testid="loan-consent-legal-mention">
                        {/* One paragraph, not two: the validity sentence must flow after the first one instead of starting on its own line */}
                        {/* No link: the consent space URL carries a token issued only once the quote is submitted, then emailed */}
                        <p className="m-0 text-[12px] font-normal leading-[20px] text-[#979797]">
                            {"En donnant votre consentement téléphonique, vous acceptez d'être contacté par téléphone par Réassurez-moi afin de recevoir des propositions adaptées à votre recherche d'assurance emprunteur. Ce consentement est valable un an et peut être retiré à tout moment depuis votre espace de gestion de consentement."}
                        </p>
                    </div>
                ) : (
                    <small className="block font-light text-[#6c757d] text-[12px] text-center">
                        En cliquant ici, j&apos;accepte que mes données personnelles soient transmises à
                        Réassurez-moi et à ses partenaires afin de traiter ma demande.
                    </small>
                )}

                {/* RGPD notice block */}
                <div className="bg-[#f5fafe] border border-[#b9d3ee] rounded-lg px-[10px] py-[20px]">
                    <div className="flex items-start gap-[15px] text-black">
                        <AppIcon iconClassName="fa-shield-halved" size="lg" className="mt-[2px] shrink-0 text-rm-blue" type="regular" />
                        <div className="flex-1 flex flex-col gap-[10px]">
                            <p className="text-[12px] font-semibold">Vos données personnelles</p>
                            <div className="flex flex-col gap-[5px]">
                                <p className="text-[11px] leading-4">
                                    Les champs suivis d&apos;un astérisque{' '}
                                    <span className="text-red-600">*</span> sont obligatoires.
                                </p>
                                <p className="text-[11px] leading-4">
                                    {"Réassurez-moi collecte vos données personnelles pour analyser votre demande et permettre la souscription éventuelle d'un contrat auprès de ses partenaires, sur la base de son intérêt légitime et/ou de mesures précontractuelles."}
                                </p>
                                {rgpdExpanded && (
                                    <p className="text-[11px] leading-4">
                                        {"Vous disposez d'un droit d'accès, de rectification, de suppression, d'opposition et de limitation. Lorsque le traitement repose sur votre consentement, vous pouvez le retirer à tout moment. Vous pouvez exercer vos droits en vous adressant à "}
                                        <a href="mailto:dpo@reassurez-moi.fr" target="_blank" rel="noopener noreferrer" className="text-[#184980] underline">
                                            dpo@reassurez-moi.fr
                                        </a>
                                        {" ou à DPO, Réassurez-moi, 19 Rue d'Aumale, 75009 Paris. Pour en savoir plus, consultez notre politique de protection des données : "}
                                        <a href="https://reassurez-moi.fr/guide/politique-confidentialite" target="_blank" rel="noopener noreferrer" className="text-[#184980] underline">
                                            politique de confidentialité
                                        </a>
                                    </p>
                                )}
                            </div>
                            <button
                                type="button"
                                onClick={() => setRgpdExpanded((v) => !v)}
                                aria-expanded={rgpdExpanded}
                                className="self-start flex items-center gap-[4px] text-[11px] underline underline-offset-2"
                            >
                                {rgpdExpanded ? 'Réduire' : 'Développer'}
                                <AppIcon iconClassName={rgpdExpanded ? 'fa-chevron-up' : 'fa-chevron-down'} size="xs" type="regular" />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Error message */}
                {error && (
                    <div className="p-3 bg-red-100 border border-red-300 rounded-lg text-red-700 text-sm">
                        {error}
                    </div>
                )}
            </form>
        </FormProvider>
    )
}
